"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Globe, Code, ShoppingCart, Briefcase, Cpu, MapPin, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const services = [{
  id: "local-seo",
  title: "Local SEO Dubai & Map Pack",
  href: "/LocalSEOPage",
  badge: "High Intent",
  description: "Dubai's 200+ nationalities and 60%+ mobile searches demand localized SEO. I help businesses capture high-intent customers across Dubai and the UAE with hyper-targeted visibility.",
  deliverables: "Google Business Profile optimization, local keyword research, geo-specific landing pages, citation building, review management.",
  icon: MapPin
}, {
  id: "technical-seo",
  title: "Technical SEO & Core Web Vitals",
  href: "/TechnicalSeoPage",
  badge: "Foundation",
  description: "I eliminate crawlability bottlenecks and rendering obstacles to guarantee search engines index your pages seamlessly and reward your performance.",
  deliverables: "Site architecture optimization, Core Web Vitals (Largest Contentful Paint, Interaction to Next Paint, Cumulative Layout Shift), structured data markup, mobile-first indexing, server response time optimization.",
  icon: Code
}, {
  id: "ecommerce-seo",
  title: "Ecommerce SEO",
  href: "/EcommerceSeoPage",
  badge: "Revenue Driven",
  description: "I build scalable online store search strategies designed to turn high-intent product queries into profitable, repeat organic transactions.",
  deliverables: "Category and product page optimization, faceted navigation structure, product schema markup and rich snippets.",
  icon: ShoppingCart
}, {
  id: "b2b-seo",
  title: "B2B SEO & Lead Generation",
  href: "/B2BSeoLeadGenerationPage",
  badge: "High Value",
  description: "I target enterprise decision-makers and high-ACV commercial queries, turning search presence into a dependable, qualified inbound pipeline.",
  deliverables: "Decision-maker keyword targeting, thought-leadership content strategy, buying-journey optimization.",
  icon: Briefcase
}, {
  id: "international-seo",
  title: "International SEO",
  href: "/InternationalSeoPage",
  badge: "Global Reach",
  description: "I scale multi-regional and multilingual architectures to help your brand compete in cross-border markets without cannibalization or indexing conflicts.",
  deliverables: "Country-specific keyword research, localization strategy, hreflang implementation, country-specific landing pages.",
  icon: Globe
}, {
  id: "ai-search",
  title: "AI Search Optimisation",
  href: "/AISearchOptimisationPage",
  badge: "Modern Search",
  description: "I optimize your brand presence for AI-generated search overviews, generative answers, and conversational search platforms.",
  deliverables: "Entity definition, knowledge graph integration, authority-led citations, and answer-engine readiness.",
  icon: Cpu
}];
export default function ServicesGrid() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicesgrid" data-nav="light" className="bg-background py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4 border border-accent/20">
            <Sparkles className="size-3.5 text-accent" />
            <span>Tailored SEO Consultancy</span>
          </div>
          
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground tracking-tight mb-6">
            Direct, Hands-On SEO Services for Measurable Growth
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            You work directly with me — no account managers, no junior delegators, and no one-size-fits-all packages. I engineer customized search strategies focused on clean execution and genuine business impact.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} data-index={i} variants={cardVariants} className="flex flex-col h-full">
                <Card className="flex flex-col h-full border border-border bg-card/60 backdrop-blur-xs rounded-xl hover:border-primary/40 hover:shadow-lg transition-[border-color,box-shadow] duration-300 group">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="secondary" className="text-xs font-medium px-2.5 py-0.5 rounded-full border border-border">
                        {service.badge}
                      </Badge>
                    </div>
                    <CardTitle className="font-serif text-xl font-bold text-foreground group-hover:text-primary transition-colors duration-200">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="flex-1 pb-6">
                    <div className="rounded-lg bg-muted/60 p-3.5 border border-border/50 text-xs text-foreground/90 space-y-1.5">
                      <div className="font-semibold text-primary flex items-center gap-1.5">
                        <CheckCircle2 className="size-3.5 text-accent shrink-0" />
                        <span>Core Deliverables:</span>
                      </div>
                      <p className="text-muted-foreground leading-normal">
                        {service.deliverables}
                      </p>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-0 border-t border-border/40 mt-auto">
                    <Link to={service.href} className="w-full">
                      <Button variant="outline" className="w-full justify-between group/btn hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors text-sm font-medium h-10">
                        <span>Explore Service Details</span>
                        <ArrowRight className="size-4 group-hover/btn:translate-x-1 transition-transform duration-200" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Callout Bar */}
        <div className="mt-14 p-6 sm:p-8 rounded-2xl bg-secondary/70 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <h3 className="font-serif text-lg sm:text-xl font-bold text-foreground">
              Need a bespoke SEO strategy or comprehensive audit?
            </h3>
            <p className="text-sm text-muted-foreground mt-1 font-sans">
              I assess your site architecture, rankings, and competitor gaps with complete transparency.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Link to="/ContactPage">
              <Button size="lg" className="font-medium">
                Book a Free Consultation
              </Button>
            </Link>
            <Link to="/CaseStudiesPage">
              <Button variant="outline" size="lg" className="font-medium">
                View Case Studies
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}